/**
 * THE RESEARCH MISSIONS CLIENT — the Owner-authored mandate the engine works
 * inside. A mission is different from a window: the window bounds a session;
 * the mission is a durable question with a deadline and a declared public
 * search plan. This client reads them and performs the three Owner acts the
 * HTTP surface already supports: authorize, continue and pause.
 */

import { readGateway, type GatewayResult } from './gateway';

export interface ResearchMission {
	missionId: string;
	status: string;
	outcome: string | null;
	deadlineAt: string;
	createdAt: string;
	instruction: string | null;
	question: string | null;
	batchesRun?: number;
	budget?: Record<string, unknown> | null;
}

export async function listResearchMissions(): Promise<
	GatewayResult<{ ok: boolean; missions?: ResearchMission[]; reason?: string }>
> {
	return readGateway('/api/scout/gateway/research/missions', {
		method: 'GET',
		timeoutMs: 30_000,
	});
}

export async function authorizeResearchMission(input: {
	question: string;
	instruction?: string;
	durationMinutes: number;
	publicSubjects: string[][];
}): Promise<GatewayResult<{ ok: boolean; mission?: ResearchMission; reason?: string }>> {
	return readGateway('/api/scout/gateway/research/missions', {
		method: 'POST',
		body: {
			question: input.question,
			...(input.instruction === undefined ? {} : { instruction: input.instruction }),
			durationMs: Math.round(input.durationMinutes * 60_000),
			publicSubjects: input.publicSubjects,
		},
		timeoutMs: 60_000,
	});
}

export async function continueResearchMission(
	missionId: string,
): Promise<
	GatewayResult<{
		ok: boolean;
		mission?: ResearchMission;
		batchesRun?: number;
		reason?: string;
	}>
> {
	return readGateway(
		`/api/scout/gateway/research/missions/${encodeURIComponent(missionId)}/continue`,
		{ method: 'POST', body: {}, timeoutMs: 120_000 },
	);
}

export async function pauseResearchMission(
	missionId: string,
): Promise<
	GatewayResult<{ ok: boolean; mission?: ResearchMission; paused?: boolean; reason?: string }>
> {
	return readGateway(
		`/api/scout/gateway/research/missions/${encodeURIComponent(missionId)}/pause`,
		{ method: 'POST', body: {}, timeoutMs: 30_000 },
	);
}
